import * as angular from "angular"
import { convertGeometryToOpenLayers, DrawingFeature, Geometry2, getDrawingFeatureEditStyle, IDataLayerZone, IGenericFeatureStyle, IMapRegularShape, models } from "../../omnia"
import { EditLayer } from "./edit-layer"
import { UndockedLabel } from "./label"

    import ZoneType = models.LayerZoneTypeId

import RegularShape = omnia.map.RegularShape
import RegularShapeFeature = omnia.map.RegularShapeFeature
import { ol_Feature, ol_geom } from "@omnia/exports/openlayers"


export class EditFeature extends DrawingFeature {

    static createFieldFeature(layer: EditLayer, fieldId: number, name: string) {

        const zone: IDataLayerZone = {
            DataLayerZoneId: null,
            LayerId: null,
            Id: null,
            Number: null,
            FieldId: fieldId,
            TypeId: ZoneType.Field,
            Label: name,
            Geometry: null,
            Value: null
        }

        return new EditFeature(layer, zone)

    }

    private _active: boolean

    private _initialShape: RegularShape
    private _initialStyle: IGenericFeatureStyle

    private _initialBuffer: {
        buffer: number
        line: ol_geom.LineString
    }

    private _udlabel: UndockedLabel


    constructor(public readonly editLayer: EditLayer, _zone: IDataLayerZone, _feature?: ol_Feature) {

        super(editLayer, _zone, _feature)

        if (_zone.RegularShape) {
            this.setInitialShape(_zone.RegularShape, editLayer.clipFeatures ? _zone.Geometry : undefined);
        }

    }

    protected setInitialShape(def: IMapRegularShape, geometry: Geometry2) {

        let shape = RegularShape.create(def);

        if (shape) {

            let shapeFeature = new RegularShapeFeature();
            shapeFeature.setShape(shape);
            this.setShape(shapeFeature);

            if (geometry) {
                this.setInitialShapeGeometry(shapeFeature.feature, geometry);
            }

        }

    }

    public setupUndockedLabel() {

        if (this._zone.LabelStyle) {
            const geometry = this.editLayer.getGeometryFromWKT(this._zone.LabelStyle.Position)
            this._udlabel = this.editLayer.createNewText(this, geometry)
        }

    }

    setActive() {

        this._active = true

        if (this._udlabel) {
            this._labelPosition = this._udlabel.geometry
            this._udlabel.visible = false
        }

        this.setInitialState()

    }

    setInactive() {

        const { feature } = this

        this._active = false
        if (this._udlabel) this._udlabel.visible = true

        if (feature) {
            feature.setStyle(null)
        }

    }

    canDelete() {
        return !this.isFieldFeature()
    }

    hasChanged(checkGeometry: boolean = true): boolean {

        if (super.hasChanged(checkGeometry)) {
            return true;
        }

        let styleChanged = !angular.equals(this._style, this._initialStyle);

        return styleChanged;

    }

    hasGeometryChanged() {

        if (!this.isRegularShape()) {
            return super.hasGeometryChanged()
        }

        if (this._initialShape) {
            return this._shape.hasChanged(this._initialShape)
        }

        return false

    }

    setInitialState() {

        super.setInitialState()

        this._initialShape = this._shape ? this._shape.shapeDefinition : null;
        this._initialStyle = angular.copy(this._style);

        this._initialBuffer = this.isBuffer()
            ? { buffer: this._buffer, line: this._bufferLine }
            : null

        return true

    }

    cancelChanges() {

        super.cancelChanges()

        if (this._shape && this._initialShape) {
            this._shape.setShape(this._initialShape)
        }

        this._style = this._initialStyle || this._style

        this.setUsePictureCoordinates()

        if (this.isBuffer() && this._initialBuffer) {
            this._buffer = this._initialBuffer.buffer
            this._bufferLine = this._initialBuffer.line
        }

        this._initialBuffer = null

        this.refresh()

        return true

    }

    private setInitialShapeGeometry(feature: ol_Feature, geometry: Geometry2) {
        const geom = convertGeometryToOpenLayers(geometry)
        feature.setGeometry(geom)
    }

    setBufferInfo(fieldId: number, line: ol_geom.LineString, geometry: ol_geom.Geometry) {
        this._fieldId = fieldId
        this.feature.setGeometry(geometry)
        this._bufferLine = line
        this.resetDimensions()
    }

    getUpdateModel() {

        const zone = super.getUpdateModel()

        if (zone && this.isFieldFeature()) {
            zone.TypeId = ZoneType.Field
            zone.FieldId = this._fieldId
            zone.Geometry = undefined
            zone.Label = undefined
            zone.LabelStyle = undefined
        }

        if (this._udlabel && this._udlabel.geometry) {
            zone.LabelStyle = {
                Position: this.layer.getGeometryAsWKT(this._udlabel.geometry)
            }
        }

        return zone

    }

    shouldSave() {

        if (this.isFieldFeature()) {
            if (!this.fillColor) return false
        }

        return true

    }

    setEditStyle() {

        const feature = this.feature
        if (!feature) return

        const style = getDrawingFeatureEditStyle(this)

        feature.setStyle(style)

    }

    getTextStyles(resolution: number, offsetY: number) {

        if (this._active || (!this.undockLabel)) {
            return super.getTextStyles(resolution, offsetY)
        }

        return []

    }

    protected onLabelChanged() {
        super.onLabelChanged()
        if (this._udlabel) this._udlabel.onLabelChanged()
    }

    canAutoLabel() {
        return this.hasDimension() && (!this.isFieldFeature())
    }

    canUndockLabel() {
        return !(this.isMarker() || this.isText() || this.isPoint() || this.isFieldFeature())
    }

    get undockLabel() {
        return !!this._udlabel
    }

    set undockLabel(value: boolean) {

        if ((!value) && this._udlabel) {
            this.editLayer.removeZone(this._udlabel)
            this._udlabel = null
            this._labelPosition = null
            this.refresh()
        }

        else if (value && (!this._udlabel)) {
            this._udlabel = this.editLayer.createNewText(this)
        }

    }

}
